import { dayKey, timeLabel } from './liveCalendar.ts';
import { zonedInstant } from './monthlyCoaching.ts';

/**
 * Decision 96: the coach's "Suggest a change" proposes one to three weekly
 * slots from their own week, as one card in the pair's thread; the player
 * accepts one, keeps theirs, or picks something else. Pure, so the words are
 * tested; the server decides every fact and the card only reads it.
 */

export const MAX_SUGGESTED = 3;

const DAY = 86_400_000;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface WeeklySlot { weekday: number; time: string }

const same = (a: WeeklySlot, b: WeeklySlot) => a.weekday === b.weekday && a.time === b.time;

/** A slot tapped in the sheet: on, or off, and never a fourth. */
export function toggleSlot(list: WeeklySlot[], s: WeeklySlot): WeeklySlot[] {
  if (list.some((x) => same(x, s))) return list.filter((x) => !same(x, s));
  return list.length >= MAX_SUGGESTED ? list : [...list, s];
}

/** "Every Tuesday at 4:00 PM": a weekly time set in its own zone, read in the viewer's. */
export function weeklyLine(w: WeeklySlot & { tz: string }, viewerTz: string, nowMs: number): string {
  // Its next date in its own zone, so the weekday and the hour cross zones together.
  for (let i = 0; i < 7; i += 1) {
    const date = dayKey(new Date(nowMs + i * DAY).toISOString(), w.tz);
    if (new Date(`${date}T12:00:00Z`).getUTCDay() !== w.weekday) continue;
    const iso = new Date(zonedInstant(date, w.time, w.tz)).toISOString();
    const weekday = new Date(`${dayKey(iso, viewerTz)}T12:00:00Z`).getUTCDay();
    return `Every ${WEEKDAYS[weekday]} at ${timeLabel(iso, viewerTz)}`;
  }
  return 'Every week';
}

export type ProposalStatus = 'open' | 'accepted' | 'kept' | 'picking' | 'replaced' | 'withdrawn';

/** The card's headline, and its line once it is answered; null while it is open. */
export function proposalLines(p: {
  status: ProposalStatus;
  /** The chosen slot's label in the viewer's zone, once accepted. */
  chosenLabel?: string | null;
  isCoach: boolean;
  coachFirst: string;
  playerFirst: string;
}): { headline: string; line: string | null } {
  const headline = p.isCoach ? 'You suggested a new weekly time' : `${p.coachFirst} suggested a new weekly time`;
  const chosen = p.chosenLabel ? p.chosenLabel.replace(/^Every/, 'every') : 'one of these times';
  switch (p.status) {
    case 'accepted': return { headline, line: p.isCoach ? `${p.playerFirst} chose ${chosen}.` : `You chose ${chosen}.` };
    case 'kept': return { headline, line: p.isCoach ? `${p.playerFirst} kept their weekly time.` : 'You kept your weekly time.' };
    case 'picking': return { headline, line: p.isCoach ? `${p.playerFirst} is picking another time.` : 'You picked another time.' };
    case 'replaced': return { headline, line: 'A newer suggestion replaced this one.' };
    default: return { headline, line: p.isCoach ? `Waiting for ${p.playerFirst}` : null };
  }
}
